import {Injectable} from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {environment} from "../../environments/environment";
import {Page} from "./types/page";
import {Observable, Subject} from "rxjs";
import {PagesListResponse} from './types/pages-list-response';
import {PageListItem} from "./types/page-list-item";

@Injectable({
  providedIn: 'root'
})
export class PageService {
  public currentPage$: Observable<Page>;

  private layoutAdded: Subject<string> = new Subject<string>();

  public sendLayoutAddedEvent(id: string): void {
    this.layoutAdded.next(id);
  }

  public getLayoutAddedEvent(): Observable<string> {
    return this.layoutAdded.asObservable();
  }

  constructor(private http: HttpClient) {
  }

  public getPageById(id: number): Observable<Page> {
    return this.http.get<Page>(`${environment.api.url}/ui/v1/page/by-id/${id}`).pipe(
      page => this.currentPage$ = page
    );
  }

  public getPageBySlug(slug: string): Observable<Page> {
    return this.http.get<Page>(`${environment.api.url}/ui/v1/page/${slug}`);
  }

  public getPages(): Observable<PagesListResponse> {
    return this.http.get<PagesListResponse>(`${environment.api.url}/ui/v1/pages`);
  }

  public delete(id: number): Observable<Page> {
    return this.http.delete<Page>(`${environment.api.url}/ui/v1/page/${id}`);
  }

  public save(id: number, page: Page): Observable<Page> {
    return this.http.post<Page>(
      `${environment.api.url}/ui/v1/page/${id}`,
      page
    )
  }

  public duplicate(id: number): Observable<Page> {
    return this.http.post<Page>(
      `${environment.api.url}/ui/v1/page/${id}/duplicate`,
      {}
    )
  }

  public movePageToFolder(pageId: number, folderId: number): Observable<Page> {
    return this.http.patch<Page>(
      `${environment.api.url}/v1/pages/${pageId}/move-to-folder/${folderId}`,
      {}
    )
  }

  // ========== TRANSLATION MANAGEMENT ==========

  /**
   * Create a translation of a page (duplicates entire page structure for new locale)
   */
  public createTranslation(pageId: number, targetLocale: string): Observable<Page> {
    return this.http.post<Page>(
      `${environment.api.url}/v1/pages/${pageId}/translate/${targetLocale}`,
      {}
    );
  }

  /**
   * Get all translations of a page
   */
  public getPageTranslations(pageId: number): Observable<{ translations: PageListItem[] }> {
    return this.http.get<{ translations: PageListItem[] }>(
      `${environment.api.url}/v1/pages/${pageId}/translations`
    );
  }

  /**
   * Link multiple existing pages as translations of each other
   */
  public linkPagesToGroup(pageIds: number[]): Observable<{ success: boolean; translationGroupId: number }> {
    return this.http.post<{ success: boolean; translationGroupId: number }>(
      `${environment.api.url}/v1/pages/link-translations`,
      { pageIds }
    );
  }

  /**
   * Remove a page from its translation group
   */
  public unlinkPageFromGroup(pageId: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.api.url}/v1/pages/${pageId}/translation-group`
    );
  }

  // ========== AUTO TRANSLATION ==========

  /**
   * Translate a page automatically and create a new translated version
   */
  public autoTranslate(pageId: number, targetLocale: string): Observable<{ success: boolean; page: PageListItem }> {
    return this.http.post<{ success: boolean; page: PageListItem }>(
      `${environment.api.url}/v1/pages/${pageId}/auto-translate/${targetLocale}`,
      {}
    );
  }

  /**
   * Get translation statistics for a page (useful for cost estimation)
   */
  public getTranslationStats(pageId: number): Observable<{
    totalTexts: number;
    htmlTexts: number;
    plainTexts: number;
    totalChars: number;
    htmlChars: number;
    plainChars: number;
  }> {
    return this.http.get<{
      totalTexts: number;
      htmlTexts: number;
      plainTexts: number;
      totalChars: number;
      htmlChars: number;
      plainChars: number;
    }>(`${environment.api.url}/v1/pages/${pageId}/translation-stats`);
  }

  // ========== PREVIEW ==========

  /**
   * Request a preview cookie for a page.
   * The cookie is set by the API response and allows previewing unpublished pages.
   */
  public requestPreviewCookie(slug: string, locale: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${environment.api.url}/ui/v1/${locale}/page/${slug}/preview`,
      {},
      { withCredentials: true }
    );
  }
}
